// Turn a raw backend/engine error string into a short explanation plus an actionable
// hint, so a failed evaluation tells the user what happened and what to try next
// instead of dumping a stack-trace-style message or a bare HTTP status.
export function describeEvaluationError(raw?: string): { message: string; hint: string } {
  const text = (raw ?? '').toLowerCase().trim();
  if (text === '') {
    return {
      message: 'Evaluation failed for an unknown reason.',
      hint: 'Try again in a moment. If it keeps failing, check the server logs.',
    };
  }
  if (text.includes('no stockfish path') || text.includes('stockfishpath')) {
    return {
      message: 'The chess engine is not configured on the server.',
      hint: 'An administrator needs to set the StockfishPath setting to a valid Stockfish binary.',
    };
  }
  if (
    text.includes('no such file') ||
    text.includes('cannot find') ||
    text.includes('not found') ||
    text.includes('permission denied') ||
    text.includes('access is denied') ||
    text.includes('cannot start')
  ) {
    return {
      message: 'The chess engine could not be started.',
      hint: 'Verify Stockfish is installed at the configured path and is executable.',
    };
  }
  if (text.includes('no fens') || text.includes('no moves') || text.includes('no positions')) {
    return {
      message: 'This game has no moves to evaluate.',
      hint: 'Re-import the game so its moves are available, then try again.',
    };
  }
  if (text.includes('readyok') || text.includes('max-tries') || text.includes('maxtries')) {
    return {
      message: 'The chess engine stopped responding during evaluation.',
      hint: 'This is usually a transient engine crash. Try evaluating the game again.',
    };
  }
  if (text.includes('cancel') || text.includes('timeout') || text.includes('timed out')) {
    return {
      message: 'Evaluation was canceled before it finished.',
      hint: 'It may have taken too long. Try again, or evaluate fewer games at once.',
    };
  }
  return {
    message: 'Evaluation failed.',
    hint: raw ?? 'Try again; if it persists, check the server logs.',
  };
}
